import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { auth, db } from './firebase';
import { firestoreSnapshotListenError } from './firestoreListenDebug';
import type {
  CrewJob,
  CrewJobCommit,
  CrewJobCommitStatus,
  CrewJobStatus,
} from '../types/crewJobs';
import { geocodeAddress } from './geocoding';
import { optionalCrewCoord, optionalCrewText } from './crewJobsMap';
import { normalizeCrewJobPayBand } from './crewJobsPay';
import { applyRosterPublicStatus, countsTowardCrewRoster } from './crewJobsRoster';
import { canConfirmCommitToRoster } from './contractorOnboard';
import { findContractorApplicantByEmail } from './contractorOnboardFirestore';

export const CREW_JOBS = 'crewJobs';
export const CREW_JOB_COMMITS = 'crewJobCommits';

export function slugifyCrewJobTitle(title: string): string {
  const base = title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
  return base || 'raise';
}

export function normalizeCrewCommitEmail(email: string): string {
  return email.trim().toLowerCase();
}

function parseDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    return (value as { toDate: () => Date }).toDate();
  }
  const d = new Date(value as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseCrewJob(id: string, raw: Record<string, unknown>): CrewJob {
  const pay = normalizeCrewJobPayBand({ unit: raw.payUnit, min: raw.payMin, max: raw.payMax });
  return {
    id,
    slug: String(raw.slug || ''),
    title: String(raw.title || ''),
    location: String(raw.location || ''),
    locationLat: optionalCrewCoord(raw.locationLat),
    locationLng: optionalCrewCoord(raw.locationLng),
    hotelName: optionalCrewText(raw.hotelName),
    hotelLocation: optionalCrewText(raw.hotelLocation),
    hotelLat: optionalCrewCoord(raw.hotelLat),
    hotelLng: optionalCrewCoord(raw.hotelLng),
    description: String(raw.description || ''),
    startDate: parseDate(raw.startDate),
    endDate: parseDate(raw.endDate),
    startTaskId: typeof raw.startTaskId === 'string' ? raw.startTaskId : null,
    endTaskId: typeof raw.endTaskId === 'string' ? raw.endTaskId : null,
    crewNeeded: typeof raw.crewNeeded === 'number' ? raw.crewNeeded : Number(raw.crewNeeded) || 0,
    payUnit: pay?.unit ?? null,
    payMin: pay?.min ?? null,
    payMax: pay?.max ?? null,
    projectId: typeof raw.projectId === 'string' ? raw.projectId : null,
    dealId: typeof raw.dealId === 'string' ? raw.dealId : null,
    projectName: typeof raw.projectName === 'string' ? raw.projectName : null,
    status: (raw.status as CrewJobStatus) || 'draft',
    releasedAt: parseDate(raw.releasedAt),
    releasedBy: typeof raw.releasedBy === 'string' ? raw.releasedBy : null,
    commitCount: typeof raw.commitCount === 'number' ? raw.commitCount : 0,
    createdAt: parseDate(raw.createdAt) || new Date(),
    updatedAt: parseDate(raw.updatedAt) || new Date(),
    createdBy: String(raw.createdBy || ''),
  };
}

function parseCrewJobCommit(id: string, raw: Record<string, unknown>): CrewJobCommit {
  return {
    id,
    jobId: String(raw.jobId || ''),
    jobSlug: String(raw.jobSlug || ''),
    jobTitle: String(raw.jobTitle || ''),
    firstName: String(raw.firstName || ''),
    lastName: String(raw.lastName || ''),
    email: String(raw.email || ''),
    phone: String(raw.phone || ''),
    note: String(raw.note || ''),
    status: (raw.status as CrewJobCommitStatus) || 'committed',
    source: raw.source === 'crm' ? 'crm' : 'website',
    contractorUserId: typeof raw.contractorUserId === 'string' ? raw.contractorUserId : null,
    internalNotes: typeof raw.internalNotes === 'string' ? raw.internalNotes : undefined,
    submittedAt: parseDate(raw.submittedAt) || new Date(),
  };
}

export async function ensureUniqueCrewJobSlug(baseSlug: string, excludeId?: string): Promise<string> {
  let candidate = baseSlug;
  let n = 0;
  while (n < 50) {
    const snap = await getDocs(query(collection(db, CREW_JOBS), where('slug', '==', candidate)));
    const conflict = snap.docs.find(d => d.id !== excludeId);
    if (!conflict) return candidate;
    n += 1;
    candidate = `${baseSlug}-${n}`;
  }
  return `${baseSlug}-${Date.now()}`;
}

export function subscribeCrewJobs(onData: (rows: CrewJob[]) => void): Unsubscribe {
  const q = query(collection(db, CREW_JOBS), orderBy('updatedAt', 'desc'));
  return onSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => parseCrewJob(d.id, d.data() as Record<string, unknown>)));
    },
    err => firestoreSnapshotListenError('crewJobsFirestore', CREW_JOBS, err),
  );
}

export function subscribeCrewJobCommits(
  jobId: string | undefined,
  onData: (rows: CrewJobCommit[]) => void,
): Unsubscribe {
  if (!jobId) {
    onData([]);
    return () => undefined;
  }
  const q = query(
    collection(db, CREW_JOB_COMMITS),
    where('jobId', '==', jobId),
    orderBy('submittedAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      onData(snap.docs.map(d => parseCrewJobCommit(d.id, d.data() as Record<string, unknown>)));
    },
    err => firestoreSnapshotListenError('crewJobsFirestore', CREW_JOB_COMMITS, err),
  );
}

export async function getCrewJob(id: string): Promise<CrewJob | null> {
  const snap = await getDoc(doc(db, CREW_JOBS, id));
  if (!snap.exists()) return null;
  return parseCrewJob(snap.id, snap.data() as Record<string, unknown>);
}

export type CrewJobInput = {
  title: string;
  location: string;
  locationLat?: number | null;
  locationLng?: number | null;
  hotelName?: string | null;
  hotelLocation?: string | null;
  hotelLat?: number | null;
  hotelLng?: number | null;
  description: string;
  startDate: Date | null;
  endDate: Date | null;
  startTaskId?: string | null;
  endTaskId?: string | null;
  crewNeeded: number;
  payUnit?: CrewJob['payUnit'];
  payMin?: number | null;
  payMax?: number | null;
  projectId?: string | null;
  dealId?: string | null;
  projectName?: string | null;
};

function payFieldsFromInput(input: {
  payUnit?: CrewJob['payUnit'];
  payMin?: number | null;
  payMax?: number | null;
}): { payUnit: CrewJob['payUnit']; payMin: number | null; payMax: number | null } {
  const pay = normalizeCrewJobPayBand({
    unit: input.payUnit,
    min: input.payMin,
    max: input.payMax,
  });
  return {
    payUnit: pay?.unit ?? null,
    payMin: pay?.min ?? null,
    payMax: pay?.max ?? null,
  };
}

async function resolveStoredCoords(
  address: string,
  hintLat?: number | null,
  hintLng?: number | null,
  existing?: { address: string; lat?: number | null; lng?: number | null },
): Promise<{ lat: number | null; lng: number | null }> {
  const trimmed = address.trim();
  if (!trimmed) return { lat: null, lng: null };
  const hintLatN = optionalCrewCoord(hintLat);
  const hintLngN = optionalCrewCoord(hintLng);
  if (hintLatN != null && hintLngN != null) return { lat: hintLatN, lng: hintLngN };
  if (
    existing &&
    trimmed === existing.address.trim() &&
    optionalCrewCoord(existing.lat) != null &&
    optionalCrewCoord(existing.lng) != null
  ) {
    return { lat: existing.lat ?? null, lng: existing.lng ?? null };
  }
  const geo = await geocodeAddress(trimmed);
  return { lat: geo?.lat ?? null, lng: geo?.lng ?? null };
}

export async function createCrewJob(input: CrewJobInput): Promise<string> {
  const uid = auth.currentUser?.uid || '';
  const slug = await ensureUniqueCrewJobSlug(slugifyCrewJobTitle(input.title));
  const now = serverTimestamp();
  const location = input.location.trim();
  const hotelName = optionalCrewText(input.hotelName);
  const hotelLocation = optionalCrewText(input.hotelLocation) || '';
  const [site, hotel] = await Promise.all([
    resolveStoredCoords(location, input.locationLat, input.locationLng),
    resolveStoredCoords(hotelLocation, input.hotelLat, input.hotelLng),
  ]);
  const ref = await addDoc(collection(db, CREW_JOBS), {
    slug,
    title: input.title.trim(),
    location,
    locationLat: site.lat,
    locationLng: site.lng,
    hotelName,
    hotelLocation: hotelLocation || null,
    hotelLat: hotel.lat,
    hotelLng: hotel.lng,
    description: input.description.trim(),
    startDate: input.startDate || null,
    endDate: input.endDate || null,
    startTaskId: input.startTaskId || null,
    endTaskId: input.endTaskId || null,
    crewNeeded: Math.max(1, Math.round(input.crewNeeded) || 1),
    ...payFieldsFromInput(input),
    projectId: input.projectId || null,
    dealId: input.dealId || null,
    projectName: input.projectName || null,
    status: 'draft',
    releasedAt: null,
    releasedBy: null,
    commitCount: 0,
    createdAt: now,
    updatedAt: now,
    createdBy: uid,
  });
  return ref.id;
}

export async function updateCrewJob(id: string, patch: Partial<CrewJobInput>): Promise<void> {
  const existing = await getCrewJob(id);
  if (!existing) throw new Error('Crew job not found');
  const payload: Record<string, unknown> = { updatedAt: serverTimestamp() };
  if (patch.title !== undefined) {
    payload.title = patch.title.trim();
    if (existing.status === 'draft') {
      payload.slug = await ensureUniqueCrewJobSlug(slugifyCrewJobTitle(patch.title), id);
    }
  }
  if (patch.location !== undefined) {
    const location = patch.location.trim();
    const site = await resolveStoredCoords(
      location,
      patch.locationLat,
      patch.locationLng,
      { address: existing.location, lat: existing.locationLat, lng: existing.locationLng },
    );
    payload.location = location;
    payload.locationLat = site.lat;
    payload.locationLng = site.lng;
  }
  if (patch.hotelName !== undefined) payload.hotelName = optionalCrewText(patch.hotelName);
  if (patch.hotelLocation !== undefined || patch.hotelLat !== undefined || patch.hotelLng !== undefined) {
    const hotelLocation = optionalCrewText(patch.hotelLocation ?? existing.hotelLocation) || '';
    const hotel = await resolveStoredCoords(
      hotelLocation,
      patch.hotelLat,
      patch.hotelLng,
      { address: existing.hotelLocation || '', lat: existing.hotelLat, lng: existing.hotelLng },
    );
    payload.hotelLocation = hotelLocation || null;
    payload.hotelLat = hotel.lat;
    payload.hotelLng = hotel.lng;
  }
  if (patch.description !== undefined) payload.description = patch.description.trim();
  if (patch.startDate !== undefined) payload.startDate = patch.startDate || null;
  if (patch.endDate !== undefined) payload.endDate = patch.endDate || null;
  if (patch.startTaskId !== undefined) payload.startTaskId = patch.startTaskId || null;
  if (patch.endTaskId !== undefined) payload.endTaskId = patch.endTaskId || null;
  if (patch.crewNeeded !== undefined) {
    payload.crewNeeded = Math.max(1, Math.round(patch.crewNeeded) || 1);
  }
  if (patch.payUnit !== undefined || patch.payMin !== undefined || patch.payMax !== undefined) {
    Object.assign(payload, payFieldsFromInput({
      payUnit: patch.payUnit !== undefined ? patch.payUnit : existing.payUnit,
      payMin: patch.payMin !== undefined ? patch.payMin : existing.payMin,
      payMax: patch.payMax !== undefined ? patch.payMax : existing.payMax,
    }));
  }
  if (patch.projectId !== undefined) payload.projectId = patch.projectId || null;
  if (patch.dealId !== undefined) payload.dealId = patch.dealId || null;
  if (patch.projectName !== undefined) payload.projectName = patch.projectName || null;
  await updateDoc(doc(db, CREW_JOBS, id), payload as DocumentData);
}

export async function setCrewJobStatus(
  id: string,
  status: CrewJobStatus,
  actorUid?: string,
): Promise<void> {
  const existing = await getCrewJob(id);
  if (!existing) throw new Error('Crew job not found');
  const payload: Record<string, unknown> = {
    status,
    updatedAt: serverTimestamp(),
  };
  if (status === 'released' && existing.status !== 'released') {
    payload.releasedAt = existing.releasedAt || serverTimestamp();
    payload.releasedBy = actorUid || auth.currentUser?.uid || existing.releasedBy || null;
  }
  if (status === 'draft') {
    payload.releasedAt = null;
    payload.releasedBy = null;
  }
  if (status === 'filled' || status === 'closed') {
    // keep releasedAt for history
  }
  await updateDoc(doc(db, CREW_JOBS, id), payload as DocumentData);
}

export async function syncCrewJobRosterCount(jobId: string): Promise<void> {
  const job = await getCrewJob(jobId);
  if (!job) return;
  const snaps = await getDocs(query(collection(db, CREW_JOB_COMMITS), where('jobId', '==', jobId)));
  const confirmed = snaps.docs.filter(d => countsTowardCrewRoster(String(d.data().status || ''))).length;
  await writeCrewJobRosterCount(job, confirmed);
}

export async function writeCrewJobRosterCount(job: CrewJob, confirmedCount: number): Promise<void> {
  const nextStatus = applyRosterPublicStatus(job.status, confirmedCount, job.crewNeeded) as CrewJobStatus;
  if (job.commitCount === confirmedCount && job.status === nextStatus) return;
  await updateDoc(doc(db, CREW_JOBS, job.id), {
    commitCount: confirmedCount,
    status: nextStatus,
    updatedAt: serverTimestamp(),
  } as DocumentData);
}

export async function updateCrewJobCommit(
  id: string,
  patch: { status?: CrewJobCommitStatus; internalNotes?: string; contractorUserId?: string | null },
): Promise<void> {
  const snap = await getDoc(doc(db, CREW_JOB_COMMITS, id));
  if (!snap.exists()) throw new Error('Commitment not found');
  const prev = parseCrewJobCommit(snap.id, snap.data() as Record<string, unknown>);
  const payload: Record<string, unknown> = {};
  if (patch.internalNotes !== undefined) payload.internalNotes = patch.internalNotes;
  if (patch.contractorUserId !== undefined) payload.contractorUserId = patch.contractorUserId;
  if (patch.status !== undefined && patch.status !== prev.status) {
    if (patch.status === 'confirmed') {
      const applicant = await findContractorApplicantByEmail(prev.email);
      if (!canConfirmCommitToRoster(applicant)) {
        throw new Error('Finish 1099 onboarding (W-9, photo ID, and signed agreement) before confirming onto the roster');
      }
    }
    payload.status = patch.status;
  }
  if (Object.keys(payload).length === 0) return;
  await updateDoc(doc(db, CREW_JOB_COMMITS, id), payload as DocumentData);
  if (payload.status) {
    await syncCrewJobRosterCount(prev.jobId);
  }
}

export async function deleteCrewJobCommit(id: string): Promise<void> {
  const snap = await getDoc(doc(db, CREW_JOB_COMMITS, id));
  if (!snap.exists()) return;
  const jobId = String(snap.data()?.jobId || '');
  await deleteDoc(doc(db, CREW_JOB_COMMITS, id));
  if (jobId) await syncCrewJobRosterCount(jobId);
}

export async function createManualCrewJobCommit(input: {
  job: CrewJob;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  note?: string;
  contractorUserId?: string | null;
}): Promise<string> {
  const firstName = input.firstName.trim();
  const lastName = input.lastName.trim();
  const email = normalizeCrewCommitEmail(input.email);
  const phone = input.phone.trim();
  if (!input.job?.id) throw new Error('Select a crew job');
  if (!firstName || !lastName) throw new Error('First and last name are required');
  if (!email) throw new Error('Email is required');
  if (!phone) throw new Error('Phone is required');

  const existing = await getDocs(
    query(
      collection(db, CREW_JOB_COMMITS),
      where('jobId', '==', input.job.id),
      where('email', '==', email),
    ),
  );
  if (!existing.empty) throw new Error('That email is already committed to this raise');

  const ref = await addDoc(collection(db, CREW_JOB_COMMITS), {
    jobId: input.job.id,
    jobSlug: input.job.slug,
    jobTitle: input.job.title,
    firstName,
    lastName,
    email,
    phone,
    note: (input.note || '').trim(),
    status: 'committed',
    source: 'crm',
    contractorUserId: input.contractorUserId || null,
    internalNotes: '',
    submittedAt: serverTimestamp(),
  });
  return ref.id;
}

export function formatCrewJobDateRange(job: Pick<CrewJob, 'startDate' | 'endDate'>): string {
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (job.startDate && job.endDate) {
    const sameDay = job.startDate.toDateString() === job.endDate.toDateString();
    return sameDay ? fmt(job.startDate) : `${fmt(job.startDate)} – ${fmt(job.endDate)}`;
  }
  if (job.startDate) return fmt(job.startDate);
  if (job.endDate) return fmt(job.endDate);
  return 'Dates TBD';
}

export function crewJobPublicPath(slug: string): string {
  return `/crew-jobs/${slug}`;
}

export function crewJobPublicUrl(slug: string): string {
  if (typeof window === 'undefined') return crewJobPublicPath(slug);
  return `${window.location.origin}${crewJobPublicPath(slug)}`;
}

export function toDateInputValue(d: Date | null | undefined): string {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseDateInput(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value.trim())) return null;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}
